import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, firstValueFrom, map } from 'rxjs';

import { environment, HttpHeadersService } from '@perval/shared';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getCognitoErrorMessage } from '../../authentification/services/cognito/cognito-errors';
import { CognitoService } from '../../authentification/services/cognito/cognito.service';
import { HeaderService } from '../../shared/design-system/organisms/p-header/p-header.service';
import { ErrorsService } from '../../shared/errors/errors.service';
import { Nullable } from '../../shared/types';
import { isEmailInvalid } from '../../shared/utils/email.utils';
import { isPhoneInvalid } from '../../shared/utils/phone.utils';
import { UserLight, Utilisateur } from '../models/utilisateur';
import {
  codeValidationHttp,
  codeValidationHttpToValidationCode,
  fromHttpToUtilisateur,
  UtilisateurHttp,
} from './mappers/utilisateur.mapper';

@Injectable({
  providedIn: 'root',
})
export class UtilisateurService {
  private readonly mainUrl = `${environment.baseApiUrl}/perval-utilisateurs`;
  constructor(
    private readonly http: HttpClient,
    private readonly httpHeadersServiceService: HttpHeadersService,
    private readonly cognitoService: CognitoService,
    private readonly errorsService: ErrorsService,
    private readonly headerService: HeaderService,
  ) {}

  public async addUtilisateur(utilisateur: Utilisateur): Promise<Utilisateur> {
    const { nom: nom, prenom: prenom, email } = utilisateur;

    return firstValueFrom(
      this.http.post<Utilisateur>(
        `${this.mainUrl}/users`,
        { nom, prenom, email },
        {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        },
      ),
    );
  }

  public async getUtilisateurById(id: string): Promise<Utilisateur> {
    return firstValueFrom(
      this.http
        .get<UtilisateurHttp>(`${this.mainUrl}/${id}`, {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        })
        .pipe(map(fromHttpToUtilisateur)),
    );
  }

  public async getUtilisateurByLogin(login: string): Promise<Utilisateur> {
    return firstValueFrom(
      this.http
        .get<UtilisateurHttp>(`${this.mainUrl}/login/${login}`, {
          headers:
            await this.httpHeadersServiceService.getHeadersWithPervalContext(),
        })
        .pipe(map(fromHttpToUtilisateur)),
    );
  }

  public async getCurrentUtilisateur(): Promise<Nullable<Utilisateur>> {
    const currentLogin = await this.cognitoService.getCurrentLogin();
    if (currentLogin) {
      return await this.getUtilisateurByLogin(currentLogin);
    }

    return null;
  }

  public getUtilisateurByCodeValidation(
    codeValidation: string,
  ): Promise<UserLight> {
    return firstValueFrom(
      this.http
        .get<codeValidationHttp>(
          `${this.mainUrl}/codeValidation/${codeValidation}`,
        )
        .pipe(map(codeValidationHttpToValidationCode)),
    );
  }

  public async updateUtilisateur(utilisateur: Utilisateur): Promise<object> {
    try {
      return firstValueFrom(
        this.http
          .put(
            `${this.mainUrl}/users`,
            { ...utilisateur },
            {
              headers:
                await this.httpHeadersServiceService.getHeadersWithPervalContext(),
            },
          )
          .pipe(
            catchError(() => {
              throw new Error(this.errorsService.getError('DEFAULT'));
            }),
          ),
      );
    } catch {
      throw new Error(this.errorsService.getError('DEFAULT'));
    }
  }

  public async validateUtilisateur(
    utilisateur: Utilisateur,
    password: string,
    passwordConfirm: string,
    type: UserLight.Type,
  ): Promise<void> {
    try {
      await firstValueFrom(
        this.http.put(`${this.mainUrl}/validate`, {
          validation_code: utilisateur.codeValidation,
          login: utilisateur.login,
          password,
          password_confirm: passwordConfirm,
          type: type,
        }),
      );
    } catch (httpError: any) {
      throw new Error(
        this.errorsService.getError(
          httpError.error.errors[0]?.message ?? 'DEFAULT',
        ),
      );
    }
  }

  public async provisioningIdNot(username: string | undefined): Promise<void> {
    if (!username) {
      throw new Error(this.errorsService.getError('DEFAULT'));
    }

    const { idToken } = (await fetchAuthSession()).tokens ?? {};
    let headers = new HttpHeaders();
    if (idToken) {
      headers = headers.set('Authorization', idToken.toString());
    }

    await firstValueFrom(
      this.http.put(
        `${this.mainUrl}/provisioning-idnot/${username}`,
        {},
        { headers },
      ),
    );
  }

  public async resetCodeValidation(codeValidation: string): Promise<void> {
    await firstValueFrom(
      this.http.post(`${this.mainUrl}/codeValidation/${codeValidation}`, {}),
    );
  }

  public isUserIdNot(user: Utilisateur): boolean {
    return user.type === Utilisateur.Type.ID_NOT;
  }

  public async updateBasicInfos(user: Utilisateur): Promise<void> {
    const { phone } = user;
    // On vérifie le format du téléphone seulement si il est renseigné afin de ne pas bloquer sa modification avec une valeur vide
    // Pour rappel le téléphone est optionnel
    if (isPhoneInvalid(phone as string) && phone !== '') {
      throw new Error(this.errorsService.getError('Phone_Not_Valid'));
    }

    try {
      await this.updateUtilisateur(user);
      this.headerService.refresh();
    } catch {
      throw new Error(this.errorsService.getError('DEFAULT'));
    }
  }

  public async updateEmail(email: string): Promise<string> {
    if (isEmailInvalid(email)) {
      throw new Error(this.errorsService.getError('Email_Not_Valid'));
    }

    try {
      await this.cognitoService.changeEmail(email);
      return email;
      // TODO : gérer les erreurs correctement dans NPER-812
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      throw new Error(getCognitoErrorMessage(String(error)));
    }
  }

  public async updateEmailIdNot(
    email: string,
    user: Utilisateur,
  ): Promise<void> {
    if (isEmailInvalid(email)) {
      throw new Error(this.errorsService.getError('Email_Not_Valid'));
    }

    try {
      await this.updateUtilisateur({
        ...user,
        email,
      });
    } catch {
      throw new Error(this.errorsService.getError('DEFAULT'));
    }
  }

  public async validateEmail(
    code: string,
    user: Utilisateur,
    newMail: string,
  ): Promise<void> {
    try {
      const validationResult =
        await this.cognitoService.validateCodeVerification(code);

      if (validationResult === 'SUCCESS') {
        await this.updateUtilisateur({
          ...user,
          email: newMail,
        } as Utilisateur);
      }
      // TODO : gérer les erreurs correctement dans NPER-812
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      throw new Error(getCognitoErrorMessage(String(error)));
    }
  }

  public async requestResetPassword(email: string): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post<Utilisateur>(`${this.mainUrl}/password/reset/request`, {
          email,
        }),
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (httpError: any) {
      throw new Error(
        this.errorsService.getError(
          httpError.error.errors[0]?.message ?? 'DEFAULT',
        ),
      );
    }
  }

  public async resetPassword(
    email: string,
    securityCode: string,
    password: string,
    passwordConfirm: string,
  ): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post<Utilisateur>(`${this.mainUrl}/password/reset/confirm`, {
          email,
          security_code: securityCode,
          password,
          password_confirm: passwordConfirm,
        }),
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (httpError: any) {
      throw new Error(
        this.errorsService.getError(
          httpError.error.errors[0]?.message ?? 'DEFAULT',
        ),
      );
    }
  }
}
